/**
 * useStudioRoom — WS connection to /ws/:sid TelerehabRoom DO.
 *
 * - Identifies on connect with Bearer access_token + role.
 * - Subscribes to room state + peer events.
 * - Exposes typed `send` for client-side messages.
 * - Auto-reconnects on disconnect (5s backoff, max 10 tries).
 * - Forwards WebRTC signaling messages via callbacks.
 */
import { useEffect, useRef, useState, useCallback } from "react";
import { refreshAccessToken } from "../lib/api.js";

export type Role = "trainer" | "case";
export type Phase = "lobby" | "live" | "paused" | "ended";

export type TaskState = "armed" | "running" | "completed" | null;

export interface RoomState {
  sessionId: string;
  phase: Phase;
  trainer: { connected: boolean; userId?: string };
  case:    { connected: boolean; userId?: string };
  consent: {
    trainer_recording: boolean;
    case_recording:    boolean;
    case_no_medical:   boolean;
  };
  source: string | null;
  game:   string | null;
  // reward removed 2026-05-09 (descoped — see ws.ts comment)
  params: Record<string, number | string | boolean>;
  // Armed-vs-running task slot (Phase B). Independent of phase: a phase=
  // live session can have taskState=null (rPPG monitoring only) and
  // transition through armed → running → completed when the trainer
  // drives a discrete task on top.
  taskState?:  TaskState;
  activeTask?: string | null;
  taskParams?: Record<string, unknown>;
  startedAt?: string;
  endedAt?: string;
}

export type ServerMsg =
  | { type: "state"; state: RoomState }
  | { type: "peer_joined"; role: Role }
  | { type: "peer_left";   role: Role }
  | { type: "session_started"; startedAt: string }
  | { type: "session_ended"; reason: string }
  | { type: "error"; code: string; message: string }
  | { type: "param_update"; key: string; value: number | string | boolean }
  | { type: "metric"; source: string; value: number; ts?: number; demo?: boolean }
  | { type: "subapp_state"; state: string; source?: string; task?: string }
  | { type: "subapp_event"; kind: string; payload?: unknown }
  | { type: "arm_task"; task: string; params?: Record<string, unknown> }
  | { type: "start_task" }
  | { type: "task_complete"; task: string; results?: Record<string, unknown> }
  | { type: "set_source"; source: string }
  | { type: "set_game";   game: string }
  | { type: "raw_preview"; channels: string[]; sps: number; samples: number[][]; ts: number }
  | { type: "webrtc_offer";  sdp: string }
  | { type: "webrtc_answer"; sdp: string }
  | { type: "ice_candidate"; candidate: unknown };

export type ClientMsg =
  | { type: "identify"; role: Role; token: string }
  | { type: "consent"; kind: "recording" | "no_medical_claim" }
  | { type: "start_session" }
  | { type: "pause" }
  | { type: "resume" }
  | { type: "end_session" }
  | { type: "emergency_stop" }
  | { type: "set_source"; source: string }
  | { type: "set_game"; game: string }
  | { type: "param_update"; key: string; value: number | string | boolean }
  | { type: "metric"; source: string; value: number; ts?: number; demo?: boolean }
  | { type: "subapp_state"; state: string; source?: string; task?: string }
  | { type: "subapp_event"; kind: string; payload?: unknown }
  | { type: "arm_task"; task: string; params?: Record<string, unknown> }
  | { type: "start_task" }
  | { type: "task_complete"; task: string; results?: Record<string, unknown> }
  | { type: "raw_preview"; channels: string[]; sps: number; samples: number[][]; ts: number }
  | { type: "webrtc_offer";  sdp: string }
  | { type: "webrtc_answer"; sdp: string }
  | { type: "ice_candidate"; candidate: unknown };

// Hostname-based env routing — see lib/api.ts resolveApiBase for context.
// staging.home.sigmacog.xyz must point at cognihome-api-staging or trainer
// + case land in different Worker DOs from the same session ID.
function resolveWsBase(): string {
  if (typeof window === "undefined") return "wss://cognihome-api.swlucifer.workers.dev";
  const h = window.location.hostname;
  if (h === "localhost" || h === "127.0.0.1") return "ws://localhost:8787";
  if (h.startsWith("staging.")) return "wss://cognihome-api-staging.swlucifer.workers.dev";
  return "wss://cognihome-api.swlucifer.workers.dev";
}
const WS_BASE = resolveWsBase();

interface UseStudioRoomOptions {
  sessionId: string;
  role: Role;
  onMessage?: (msg: ServerMsg) => void;
}

export function useStudioRoom({ sessionId, role, onMessage }: UseStudioRoomOptions) {
  const [state, setState]       = useState<RoomState | null>(null);
  const [connected, setConn]    = useState(false);
  const [error, setError]       = useState<string | null>(null);
  const wsRef                   = useRef<WebSocket | null>(null);
  const retryRef                = useRef(0);
  const onMessageRef            = useRef(onMessage);
  onMessageRef.current = onMessage;

  const send = useCallback((msg: ClientMsg) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(msg));
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    let reconnectTimer: number | null = null;
    let releaseLock: (() => void) | null = null;

    function open() {
      if (cancelled) return;
      const token = sessionStorage.getItem("cognihome_access_token");
      if (!token) {
        setError("missing_token");
        return;
      }
      const ws = new WebSocket(`${WS_BASE}/ws/${sessionId}`);
      wsRef.current = ws;

      ws.onopen = () => {
        retryRef.current = 0;
        setConn(true);
        setError(null);
        ws.send(JSON.stringify({ type: "identify", role, token } satisfies ClientMsg));
      };

      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data as string) as ServerMsg;
          if (msg.type === "state") setState(msg.state);
          if (msg.type === "error") {
            setError(`${msg.code}: ${msg.message}`);
            // P7: on bad_token, try refresh + reconnect immediately.
            // The DO closes the WS shortly after sending bad_token, so
            // we'll go through onclose → reconnect anyway, but kicking
            // off the refresh now means the next identify uses fresh
            // creds without the 5 s default backoff.
            if (msg.code === "bad_token") {
              refreshAccessToken().then(t => {
                if (t && !cancelled) {
                  // Force-close the current ws so the reconnect path
                  // fires with new token in storage.
                  try { ws.close(4000, "token-refreshed"); } catch { /* */ }
                  retryRef.current = 0;
                }
              });
            }
          }
          onMessageRef.current?.(msg);
        } catch { /* malformed; ignore */ }
      };

      ws.onclose = (ev) => {
        setConn(false);
        wsRef.current = null;
        if (cancelled) return;
        // 1008 / custom 4000 = our explicit force-close after refresh.
        // Reconnect immediately; otherwise back off 5 s × up-to-10.
        const fast = ev.code === 4000;
        if (retryRef.current < 10) {
          retryRef.current++;
          reconnectTimer = window.setTimeout(open, fast ? 200 : 5000);
        }
      };

      ws.onerror = () => { /* close handler will fire */ };
    }

    // P8-2 (M5 dedup): wrap WS connect in navigator.locks so only one tab
    // per (sessionId, role) actually opens a WS. Latecomer tabs see
    // ifAvailable=true → null lock → set error and don't connect, sparing
    // DO from the trainer/case-tab pingpong (handleIdentify drops stale
    // peer; without lock, two tabs would constantly re-identify and close
    // each other every 5s reconnect cycle).
    function tryOpen() {
      if (typeof navigator !== "undefined" && (navigator as Navigator & { locks?: LockManager }).locks) {
        navigator.locks
          .request(
            `cognihome-session-${sessionId}-${role}`,
            { mode: "exclusive", ifAvailable: true },
            (lock) => {
              if (!lock) {
                // Another tab has the session open. Surface as error so the
                // caller can render a "請先關閉另一個分頁" banner.
                setError("session_locked: 此 session 已在另一個分頁開啟");
                return Promise.resolve();
              }
              setError((prev) => prev?.startsWith("session_locked") ? null : prev);
              open();
              return new Promise<void>((resolve) => {
                releaseLock = resolve;
              });
            },
          )
          .catch(() => { /* lock failed — fall through and just open */ open(); });
      } else {
        open();   // browser without Web Locks API
      }
    }

    tryOpen();

    return () => {
      cancelled = true;
      if (reconnectTimer) window.clearTimeout(reconnectTimer);
      wsRef.current?.close(1000, "unmount");
      wsRef.current = null;
      releaseLock?.();
    };
  }, [sessionId, role]);

  return { state, connected, error, send };
}
